import { ThemeProvider, useTheme } from '@mui/material/styles';
import React, { useMemo } from 'react';

import { useChartLocale } from 'components/charts/hooks/use-chart-locale';
import { useChartMode } from 'components/charts/hooks/use-chart-mode';
import { createChartTheme } from 'components/charts/theme/create-chart-theme';
import { useOptionalChartSettings } from 'contexts/chart/chart-settings-context';
import { ChartSurfaceProvider } from 'contexts/chart/chart-surface-context';
import { ChartSurfaceProps } from 'components/charts/types/chart-surface.type';

type Props = ChartSurfaceProps & {
  children: React.ReactNode;
};

const ChartThemeScope: React.FC<Props> = ({ children, dark }) => {
  const outerTheme = useTheme();
  const chartSettings = useOptionalChartSettings();
  const localization = useChartLocale();
  // Shared charts use the application theme captured before any feature-scoped
  // theme, so a preview shell cannot override their chart palette.
  const baseTheme = chartSettings?.baseTheme ?? outerTheme;
  const settings = chartSettings?.settings;
  const mode = useChartMode(dark);
  const chartTheme = useMemo(
    () => createChartTheme(baseTheme, settings, localization, mode),
    [baseTheme, settings, localization, mode]
  );

  // Re-providing the surface lets content inside the chart, such as SVG children, follow its `dark` prop.
  return (
    <ChartSurfaceProvider dark={dark}>
      <ThemeProvider theme={chartTheme}>{children}</ThemeProvider>
    </ChartSurfaceProvider>
  );
};

export default ChartThemeScope;
